import React, { useState } from "react";
import { db, auth, storage } from "../../firebase/config";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { useLoadScript } from "@react-google-maps/api";
import { requireVerifiedEmail } from "../../utils/emailVerification";
import { EVENT_REGISTRATION_OPTIONS } from "../../constants/availability";

const DEFAULT_BOOTH_CATEGORIES = [
  { id: "craft_vendor", label: "Craft Vendor", spotsAvailable: 0, spotsReserved: 0 },
  { id: "food_vendor", label: "Food Vendor", spotsAvailable: 0, spotsReserved: 0 },
  { id: "produce_vendor", label: "Produce Vendor", spotsAvailable: 0, spotsReserved: 0 },
  { id: "livestock_vendor", label: "Livestock Vendor", spotsAvailable: 0, spotsReserved: 0 },
  { id: "general_vendor", label: "General Vendor", spotsAvailable: 0, spotsReserved: 0 },
];

const LIBRARIES = ["places"];

const AddEvent = ({ userProfile }) => {
  const navigate = useNavigate();
  const user = auth.currentUser;
  const { isLoaded, loadError } = useLoadScript({
  googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  libraries: LIBRARIES,
});

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("county_fair");
  const [eventDate, setEventDate] = useState("");
  const [locationText, setLocationText] = useState("");
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [reservationEnabled, setReservationEnabled] = useState(false);
  const [boothCategories, setBoothCategories] = useState(DEFAULT_BOOTH_CATEGORIES);
  const [registrationStatus, setRegistrationStatus] = useState("open");
  const [registrationOpensAt, setRegistrationOpensAt] = useState("");
  const [detailsComingSoon, setDetailsComingSoon] = useState(false);
  const [detailsNote, setDetailsNote] = useState("");

  const geocodeAddress = (address) => {
    return new Promise((resolve, reject) => {
      if (!window.google?.maps) {
        reject(new Error("Google Maps is not loaded yet."));
        return;
      }

      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ address }, (results, status) => {
        if (status !== "OK" || !results?.[0]) {
          reject(new Error(`Could not find that location: ${status}`));
          return;
        }

        const location = results[0].geometry.location;
        const components = results[0].address_components || [];

        const city =
          components.find((c) => c.types.includes("locality"))?.long_name ||
          components.find((c) => c.types.includes("administrative_area_level_2"))
            ?.long_name ||
          "";

        const state =
          components.find((c) => c.types.includes("administrative_area_level_1"))
            ?.short_name || "";

        resolve({
          lat: location.lat(),
          lng: location.lng(),
          city,
          state,
          formattedAddress: results[0].formatted_address || address,
        });
      });
    });
  };

  const updateBoothSpots = (id, value) => {
  setBoothCategories((prev) =>
    prev.map((cat) =>
      cat.id === id
        ? {
            ...cat,
            spotsAvailable:
              value === "" ? "" : Math.max(0, Number(value) || 0),
          }
        : cat
    )
  );
};

  const handleSubmit = async () => {
    if (!user) return alert("Login required");
    if (!requireVerifiedEmail(user, userProfile)) return;

    if (!title || !eventDate || !locationText) {
      return alert("Title, date, and location required");
    }

    const activeBoothCategories = boothCategories.filter(
      (cat) => Number(cat.spotsAvailable || 0) > 0
    );

    if (reservationEnabled && activeBoothCategories.length === 0) {
      return alert("Add at least one booth category with available spots.");
    }

    if (reservationEnabled && registrationStatus !== "open") {
      const confirmed = window.confirm(
        "Registration is not open yet. Post the event now with reservations disabled until you manually open registration?"
      );

      if (!confirmed) return;
    }

    try {
      setUploading(true);

      if (loadError) {
  return alert("Google Maps failed to load. Please refresh and try again.");
}

if (!isLoaded || !window.google?.maps) {
  return alert("Google Maps is still loading. Please wait a second and try again.");
}

      const locationDetails = await geocodeAddress(locationText);

      let imageUrl = "";

      if (image) {
        const imageRef = ref(storage, `events/${user.uid}_${Date.now()}`);
        await uploadBytes(imageRef, image);
        imageUrl = await getDownloadURL(imageRef);
      }

      await addDoc(collection(db, "communityEvents"), {
        ownerId: user.uid,
        title: title.trim(),
        description: description.trim(),
        category,
        eventDate,
        locationText: locationDetails.formattedAddress || locationText.trim(),
        city: locationDetails.city,
        state: locationDetails.state,
        lat: locationDetails.lat,
        lng: locationDetails.lng,
        image: imageUrl,

        reservationEnabled,
        paymentMethod: reservationEnabled ? "pay_in_person" : "none",
        registrationStatus,
        registrationOpensAt:
          registrationStatus === "opens_later" ? registrationOpensAt : "",
        detailsComingSoon,
        detailsNote: detailsComingSoon ? detailsNote.trim() : "",
        boothCategories: reservationEnabled ? activeBoothCategories : [],
        totalSpotsAvailable: reservationEnabled
          ? activeBoothCategories.reduce(
              (sum, cat) => sum + Number(cat.spotsAvailable || 0),
              0
            )
          : 0,
        totalSpotsReserved: 0,

        createdAt: serverTimestamp(),
        status: "active",
        isArchived: false,
      });

      alert("Event posted 🎉");
      navigate("/map");
    } catch (err) {
      console.error("Error posting event:", err);
      alert(err.message || "Error posting event");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="card page-shell page-shell-narrow">
      <h2>Add Community Event</h2>
      <p className="page-subtitle">
        Post fairs, markets, livestock shows, auctions, and local farm events.
      </p>

      <div className="form-stack">
        <input
          placeholder="Event Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <textarea
          placeholder="Event Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={5}
        />

        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="county_fair">County Fair</option>
          <option value="craft_fair">Craft Fair</option>
          <option value="livestock_show">Livestock Show</option>
          <option value="livestock_sale">Livestock Sale</option>
          <option value="farmers_market">Farmers Market</option>
          <option value="equipment_auction">Equipment Auction</option>
          <option value="community_event">Community Event</option>
          <option value="other">Other</option>
        </select>

        <input
          type="date"
          value={eventDate}
          onChange={(e) => setEventDate(e.target.value)}
        />

        <input
          placeholder="Location, City, State"
          value={locationText}
          onChange={(e) => setLocationText(e.target.value)}
        />

        <input
          type="file"
          accept="image/*"
          onChange={(e) => setImage(e.target.files[0])}
        />

        <div className="form-panel">
          <label className="form-label">
            Registration timing
            <select
              value={registrationStatus}
              onChange={(e) => setRegistrationStatus(e.target.value)}
            >
              {EVENT_REGISTRATION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {registrationStatus === "opens_later" && (
            <label className="form-label">
              Registration opens optional
              <input
                type="date"
                value={registrationOpensAt}
                onChange={(e) => setRegistrationOpensAt(e.target.value)}
              />
            </label>
          )}

          <label className="auth-checkbox">
            <input
              type="checkbox"
              checked={detailsComingSoon}
              onChange={(e) => setDetailsComingSoon(e.target.checked)}
            />
            <span>Details coming soon</span>
          </label>

          {detailsComingSoon && (
            <label className="form-label">
              Details note optional
              <textarea
                value={detailsNote}
                onChange={(e) => setDetailsNote(e.target.value)}
                rows={3}
                placeholder="Example: Vendor map and booth details will be posted after permits are finalized."
              />
            </label>
          )}

          <label className="auth-checkbox">
            <input
              type="checkbox"
              checked={reservationEnabled}
              onChange={(e) => setReservationEnabled(e.target.checked)}
            />
            <span>Allow people to reserve vendor spots 🎪</span>
          </label>



          {reservationEnabled && (
            <div style={{ marginTop: "14px" }}>
              <p className="page-subtitle">
                Vendors will request a spot first. You can accept them after they
                pay in person.
              </p>

              <div className="form-stack" style={{ marginTop: "14px" }}>
                {boothCategories.map((cat) => (
                  <div key={cat.id} className="card-lite">
                    <label className="form-label">{cat.label} Spots</label>
                    <input
  type="number"
  inputMode="numeric"
  min="0"
  value={cat.spotsAvailable === 0 ? "" : cat.spotsAvailable}
  onChange={(e) => updateBoothSpots(cat.id, e.target.value)}
  placeholder="0"
/>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <button
  type="button"
  className="btn-primary"
  onClick={handleSubmit}
  disabled={uploading || !isLoaded}
>
  {uploading ? "Posting..." : !isLoaded ? "Loading Maps..." : "Post Event"}
</button>
      </div>
    </div>
  );
};

export default AddEvent;
